import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@hubertai/hubert-component-library";
import { changeViewerPassword, linkViewerPassword } from "../../behavior/api/viewer";
import { getAccessToken, storeTokens } from "../../behavior/security/auth.service";
import { PageState, ProviderId } from "../../constants";
import type { JwtModel } from "../../types/authentication";
import { encodeParam, generateGoogleSignInUrl } from "../../utils/urlUtility";
import { getChangePasswordSchema } from "../../utils/validationSchemas";
import { Alert, Button, HorizontalLine, Text } from "../elements";
import { Password } from "../forms";

interface ChangePasswordProps {
    existingPassword: boolean;
    primaryProvider?: string;
    loading: boolean;
    refetch: () => Promise<void>;
}

const ButtonType = {
    CHANGE_PASSWORD: "CHANGE_PASSWORD",
    SET_PASSWORD: "SET_PASSWORD",
    SIGNIN_AGAIN: "SIGNIN_AGAIN",
} as const;

type TButtonType = (typeof ButtonType)[keyof typeof ButtonType];

const ChangePassword = ({ loading, existingPassword, primaryProvider = "", refetch }: ChangePasswordProps) => {
    const { t } = useTranslation();

    const initialValues = {
        currentPassword: "",
        newPassword: "",
        repeatNewPassword: "",
    };

    const { mutateAsync: changeUserPassword, isPending: changePasswordLoading } = useMutation({
        mutationFn: changeViewerPassword,
    });

    const { mutateAsync: linkUserPassword, isPending: linkPasswordLoading } = useMutation({
        mutationFn: linkViewerPassword,
    });

    const isOldSession = () => {
        const accessToken = getAccessToken();
        if (accessToken) {
            const decodedToken = jwtDecode<JwtModel>(accessToken);
            const authValidTime = decodedToken.auth_time + 3 * 60;
            const current = Math.floor(new Date().getTime() / 1000.0);
            return current > authValidTime;
        }
        return false;
    };

    const [buttonType, setButtonType] = useState<TButtonType>(
        isOldSession() ? ButtonType.SIGNIN_AGAIN : ButtonType.SET_PASSWORD,
    );

    useEffect(() => {
        if (existingPassword) {
            setButtonType(ButtonType.CHANGE_PASSWORD);
        }
    }, [existingPassword]);

    const googleSignIn = (loginState: string) => {
        const googleSignInUrl = generateGoogleSignInUrl(loginState);
        window.location.href = googleSignInUrl.toString();
    };

    const redirectUserToSignIn = () => {
        const loginState = encodeParam({ action: PageState.LINK_PASSWORD });
        switch (primaryProvider) {
            case ProviderId.GOOGLE:
                googleSignIn(loginState);
                break;
            default:
                toast.info("Unsupported provider");
                break;
        }
    };

    const linkPassword = async (newPassword: string) => {
        const response = await linkUserPassword({ newPassword });

        if (response.type === "CREDENTIAL_TOO_OLD" || response.message === "Error_Old_Credential") {
            window.location.reload();
            return;
        }

        if (!response.access_token) {
            toast.error(t("AppMessages.server-error"));
            return;
        }

        storeTokens(response);
        await refetch();
        resetForm();
        toast.success(t("MyProfile.ChangePassword.Success_Password_Changed"));
    };

    const updatePassword = async (oldPassword: string, newPassword: string) => {
        const response = await changeUserPassword({ oldPassword, newPassword });

        if (response.type === "INVALID_PASSWORD" || response.message === "Error_Invalid_CurrentPassword") {
            const message = response.message;
            toast.error(t(`MyProfile.ChangePassword.${message}`));
            return;
        }

        if (!response.access_token) {
            toast.error(t("AppMessages.server-error"));
            return;
        }

        storeTokens(response);
        await refetch();
        resetForm();
        toast.success(t("MyProfile.ChangePassword.Success_Password_Changed"));
    };

    const { values, errors, touched, isValid, handleSubmit, handleChange, handleBlur, resetForm } = useFormik({
        initialValues,
        validationSchema: buttonType !== ButtonType.SIGNIN_AGAIN ? getChangePasswordSchema(existingPassword) : null,
        onSubmit: async (values) => {
            try {
                if (isOldSession() && buttonType === ButtonType.SET_PASSWORD) {
                    window.location.reload();
                    return;
                }

                if (buttonType === ButtonType.SIGNIN_AGAIN) {
                    redirectUserToSignIn();
                    return;
                }

                if (existingPassword) {
                    return await updatePassword(values.currentPassword, values.newPassword);
                } else {
                    return await linkPassword(values.newPassword);
                }
            } catch (e) {
                toast.error((e as Error).message);
            }
        },
    });

    const buttonText = () => {
        switch (buttonType) {
            case ButtonType.CHANGE_PASSWORD:
                return t("MyProfile.ChangePassword.ButtonText_UpdatePassword");
            case ButtonType.SIGNIN_AGAIN:
                return t("MyProfile.ChangePassword.ButtonText_SignInAgain");
            default:
                return t("MyProfile.ChangePassword.ButtonText_SetPassword");
        }
    };

    const disableButton = () => {
        if (buttonType === ButtonType.SIGNIN_AGAIN) {
            return false;
        }
        return !isValid || changePasswordLoading || linkPasswordLoading;
    };

    return (
        <div className="change-password-wrapper">
            <Text size="large" weight="semibold">
                {existingPassword
                    ? t("MyProfile.ChangePassword.Title")
                    : t("MyProfile.ChangePassword.Set_Password_Title")}
            </Text>
            <HorizontalLine weight="semi-dark" />
            <form className="change-password-form" onSubmit={handleSubmit}>
                {existingPassword && (
                    <Password
                        label={t("MyProfile.ChangePassword.Label_CurrentPassword")}
                        name="currentPassword"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.currentPassword}
                        error={errors.currentPassword}
                        touched={touched.currentPassword}
                        loading={loading}
                    />
                )}
                <Password
                    label={t("MyProfile.ChangePassword.Label_NewPassword")}
                    name="newPassword"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    value={values.newPassword}
                    error={errors.newPassword}
                    touched={touched.newPassword}
                    loading={loading}
                    disabled={buttonType === ButtonType.SIGNIN_AGAIN}
                />
                <Password
                    label={t("MyProfile.ChangePassword.Label_RepeatNewPassword")}
                    name="repeatNewPassword"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    value={values.repeatNewPassword}
                    error={errors.repeatNewPassword}
                    touched={touched.repeatNewPassword}
                    loading={loading}
                    disabled={buttonType === ButtonType.SIGNIN_AGAIN}
                />
                {buttonType === ButtonType.SIGNIN_AGAIN ? (
                    <Alert type="info" className="custom-alert">
                        <Text className="me-2">{t("MyProfile.ChangePassword.Alert_SignInAgain")}</Text>
                    </Alert>
                ) : null}
                <Button submit className="mt-4" hidden={loading} disabled={disableButton()}>
                    {buttonText()}
                </Button>
            </form>
        </div>
    );
};

export default ChangePassword;
